import type {
  PrescreenFields,
  PrescreenFieldKeys,
} from '../../agent-profile/contact-info/contact-agent/prescreen-form'
import type { ScheduleTourFormData } from './schedule-tour-form-wrapper'

import { Icon } from '@rentspree/component-v2/dist/icon'
import { buildPath } from '@rentspree/path'
import { set } from 'lodash'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMedia } from 'react-use'

import { useTracker } from '@hooks'
import { Body, ButtonLink, Text } from 'components'
import { PRESCREEN_FIELDS } from 'constant'
import { Wizard } from 'containers/wizard'
import { WizardSubSectionText } from 'containers/wizard/wizard.styled'
import { ListingIdContext } from 'context/listingIdContext'
import { selectAgentProfile } from 'store/agent-profile'
import {
  selectPublicListingAgentContactDisplayName,
  selectTextAddress,
  selectPropertyId,
} from 'store/public-listing'
import { selectScheduleTourApiStatus } from 'store/public-messaging'
import { asyncActions } from 'store/public-messaging/async-actions'
import { actions as toastActions } from 'store/toast'
import { selectUserProfile, selectUserProfileUserType } from 'store/users'
import { LISTING_PUBLIC_PAGE } from 'tracker/events'

import { env } from 'process'

import {
  ListingScheduleTourWrapper,
  ScheduleTourInformationAlert,
  CloseButton,
} from './listing-schedule-tour.styled'
import {
  ScheduleTourFormWrapper,
  ScheduleTourOptionalFormWrapper,
} from './schedule-tour-form-wrapper'
import { openSweetAlertForLoggedIn, openSweetAlertForAnonymousUser } from './successSweetAlert'

export type PrescreenMessageData = {
  answerSheet: [{ question: string; answer: string }]
  propertyId: string
}

export type ScheduleTourMessageData = {
  preferredDate: string[]
  propertyAddress: string
}

export type RequestScheduleTourMessage = {
  data: string | PrescreenMessageData | ScheduleTourMessageData
  type: string
}

type ScheduleTourWizardProps = {
  handleCloseModal: () => void
}

export const ScheduleTourWizard: React.FC<ScheduleTourWizardProps> = (props) => {
  const { handleCloseModal } = props

  const [step, setStep] = useState<number>(1)
  const [formData, setFormData] = useState<ScheduleTourFormData>()
  const apiRequestStatus = useRef<boolean>(false)
  const dispatch = useDispatch()
  const userProfile = useSelector(selectUserProfile) || {}
  const propertyId = useSelector(selectPropertyId)
  const userType = useSelector(selectUserProfileUserType)
  const agentContactName = useSelector(selectPublicListingAgentContactDisplayName)
  const apiResponseStatus = useSelector(selectScheduleTourApiStatus)
  const propertyAddress = useSelector(selectTextAddress)
  const { userId, email: agentEmail } = useSelector(selectAgentProfile) || {}
  const isMobile = useMedia('(max-width: 991px)')
  const track = useTracker()
  const listingId = useContext(ListingIdContext)

  useEffect(() => {
    const openSweetAlert = (): void => {
      if (userProfile?.id) openSweetAlertForLoggedIn(userType, formData?.selectedDates as string[])
      else openSweetAlertForAnonymousUser(formData?.selectedDates as string[])
    }
    if (apiRequestStatus.current === true) {
      switch (apiResponseStatus) {
        case 'fulfilled':
          handleCloseModal()
          setStep(1)
          setFormData(undefined)
          apiRequestStatus.current = false
          openSweetAlert()
          break
        case 'rejected':
          dispatch(
            toastActions.addToast({
              status: 'error',
              titleMessage: 'Something went wrong while sending message. Please try again.',
              bodyMessage: '',
            }),
          )
          handleCloseModal()
          setStep(1)
          setFormData(undefined)
          apiRequestStatus.current = false
          break
        default:
          break
      }
    }
  }, [dispatch, formData?.selectedDates, props, apiResponseStatus, userProfile?.id, userType])

  const getDefaultMessage = () =>
    userProfile?.id
      ? `Hi ${agentContactName}, 
I saw your listing and I’d like to schedule a tour for ${propertyAddress}. Please let me know if you’re available for a showing.`
      : `Hi ${agentContactName}, 
I’m interested in learning more about ${propertyAddress}. Could you please contact me?`

  const parsePrescreenFormData = (prescreenFormData: PrescreenFields) =>
    Object.keys(PRESCREEN_FIELDS).reduce((result, key) => {
      const prescreenKey = key as PrescreenFieldKeys
      const prescreenAnswer = prescreenFormData[prescreenKey]

      if (prescreenAnswer) {
        const answerSheetData = {
          question: PRESCREEN_FIELDS[prescreenKey].label,
          answer: prescreenAnswer,
        }
        if (!result.answerSheet) {
          set(result, 'answerSheet', [answerSheetData])
        } else {
          result.answerSheet?.push(answerSheetData)
        }
      }
      return result
    }, {} as PrescreenMessageData)

  const createPayload = (prescreenData: PrescreenMessageData) => {
    if (formData) {
      const { fullName, email, phone } = formData?.scheduleTourData
      let { message } = formData?.scheduleTourData
      if (!message) {
        message = getDefaultMessage()
      }
      const messages: RequestScheduleTourMessage[] = [{ data: message, type: 'msg' }]

      if (prescreenData.answerSheet) {
        messages.push({
          data: { ...prescreenData, propertyId },
          type: 'pre-screen',
        })
      }

      messages.push({
        data: { preferredDate: formData.selectedDates, propertyAddress },
        type: 'schedule-tour',
      })

      return {
        sender: {
          fullName,
          email,
          phone: phone || undefined,
        },
        recipient: {
          _id: userId || '',
          email: agentEmail || '',
        },
        messages,
        source: 'listing-syndication',
      }
    }
    return undefined
  }

  const redirectToLogin = () => {
    window.location.assign(
      buildPath(`${env.BASE_URL}/account/login`, {}, { continue: window.location.href }),
    )
  }

  const handleNextStep = () => {
    setStep(step + 1)
  }

  const handlePreviousStep = () => {
    setStep(step - 1)
  }

  const handleSubmitStep1 = (data: ScheduleTourFormData) => {
    setFormData(data)
    track(LISTING_PUBLIC_PAGE.CLICK_CONTINUE_TOUR_MODAL, {
      date_selected: data?.selectedDates?.length,
      listing_id: listingId,
    })
    handleNextStep()
  }

  const handleSubmitStep2 = (data: PrescreenFields) => {
    const parsePrescreenData = parsePrescreenFormData(data)
    const payload = createPayload(parsePrescreenData)
    const property = { id: propertyId, address: propertyAddress }
    if (payload) {
      void dispatch(
        asyncActions.sendScheduleTourRequestToAgent({
          ...payload,
          source: 'listing-syndication',
          property,
        }),
      )
      apiRequestStatus.current = true
    }
    track(LISTING_PUBLIC_PAGE.SUBMIT_TOUR_REQUEST, { listing_id: listingId })
    track(LISTING_PUBLIC_PAGE.SUBMIT_PRE_SCREEN, {
      Q1: data.moveInDate ? 'Y' : 'N',
      Q2: data.preferredLeaseTerm ? 'Y' : 'N',
      Q3: data.householdMonthlyIncome ? 'Y' : 'N',
      Q4: data.occupy ? 'Y' : 'N',
      listing_id: listingId,
    })
  }

  const closeScheduleTourModal = () => {
    setFormData(undefined)
    handleCloseModal()
    setStep(1)
  }
  return (
    <ListingScheduleTourWrapper data-testid="schedule-tour-wrapper">
      <CloseButton id="modal-close-btn" onClick={closeScheduleTourModal}>
        <Icon icon="close" size="md" />
      </CloseButton>
      <Wizard
        step={step}
        title="Request a Tour"
        subSection={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <>
            <WizardSubSectionText $hide={step === 1}>
              Let your agent know more about you <Text>(optional)</Text>
            </WizardSubSectionText>
            {!userProfile?.id && (
              <ScheduleTourInformationAlert
                data-testid="login-banner"
                variant="info"
                noClose
                $mMargin="0 0 24px"
              >
                <Body $textStyle="body-small" $mTextStyle="body" $block>
                  Already have an account?{' '}
                  <ButtonLink
                    text="Login"
                    $textStyle="body-small"
                    $mTextStyle="body"
                    $bold
                    $underline
                    onClick={redirectToLogin}
                  />{' '}
                  to chat via direct messages.
                </Body>
              </ScheduleTourInformationAlert>
            )}
          </>
        }
      >
        <ScheduleTourFormWrapper
          defaultMessage={getDefaultMessage()}
          onSubmit={handleSubmitStep1}
          profile={userProfile}
          formData={formData}
          isMobile={isMobile}
        />
        <ScheduleTourOptionalFormWrapper
          onSubmit={handleSubmitStep2}
          onClickPrevious={handlePreviousStep}
        />
      </Wizard>
    </ListingScheduleTourWrapper>
  )
}
